import { useState, useEffect, useCallback } from 'react';
import { supplyAPI } from '../../api';
import toast from 'react-hot-toast';
import {
  LineChart, Line, AreaChart, Area,
  PieChart, Pie, Cell, Tooltip,
  ResponsiveContainer, XAxis, YAxis, CartesianGrid, Legend,
} from 'recharts';

// ── Shared styles ────────────────────────────────────────────
const S = {
  shell:    { display:'flex', height:'100vh', fontFamily:'DM Sans, sans-serif', background:'#f5f6fa' },
  sidebar:  { width:200, background:'#fff', borderRight:'1px solid #e2e8f0', display:'flex', flexDirection:'column', flexShrink:0 },
  sideHead: { padding:'16px 18px', borderBottom:'1px solid #e2e8f0', fontWeight:800, fontSize:15, color:'#1e2a3b' },
  navBtn:   (active) => ({
    display:'block', width:'100%', textAlign:'left',
    padding:'11px 18px', border:'none', cursor:'pointer',
    fontFamily:'DM Sans, sans-serif', fontSize:13,
    background: active ? '#f0f4ff' : 'transparent',
    color:       active ? '#1a56db' : '#4a5568',
    fontWeight:  active ? 600 : 400,
    borderLeft:  active ? '3px solid #1a56db' : '3px solid transparent',
  }),
  main:     { flex:1, display:'flex', flexDirection:'column', overflow:'hidden' },
  topbar:   { height:48, background:'var(--theme-header-bg,#1a56db)', display:'flex', alignItems:'center', padding:'0 20px', flexShrink:0 },
  topTitle: { color:'var(--theme-header-text,#fff)', fontWeight:700, fontSize:14 },
  content:  { flex:1, overflowY:'auto', padding:'24px 28px' },
  card:     { background:'#fff', border:'1px solid #e2e8f0', borderRadius:10, padding:'20px 24px' },
  tbl:      { width:'100%', borderCollapse:'collapse', fontSize:13 },
  th:       { padding:'10px 12px', background:'#f8fafc', borderBottom:'1px solid #e2e8f0', textAlign:'left', fontWeight:600, color:'#374151', fontSize:12, whiteSpace:'nowrap' },
  td:       { padding:'10px 12px', borderBottom:'1px solid #f1f5f9', color:'#374151', fontSize:13 },
  badge:    (color) => ({ display:'inline-block', padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:600, background:color+'22', color }),
  btn:      { padding:'7px 16px', borderRadius:7, border:'none', cursor:'pointer', fontSize:13, fontWeight:600, fontFamily:'DM Sans,sans-serif' },
  input:    { padding:'7px 10px', border:'1px solid #d1d5db', borderRadius:6, fontSize:13, fontFamily:'DM Sans,sans-serif' },
  select:   { padding:'7px 10px', border:'1px solid #d1d5db', borderRadius:6, fontSize:13, fontFamily:'DM Sans,sans-serif', background:'#fff' },
  label:    { fontSize:12, color:'#6b7280', marginRight:6 },
};

const fmt = (n) => n ? `INR ${Number(n).toLocaleString('en-IN')}` : '—';
const fmtK = (n) => n >= 1000 ? `${(n/1000).toFixed(2)}K` : String(n);

const STATUS_COLOR = {
  Booked: '#16a34a', Pending: '#d97706', Rejected: '#dc2626', Paid: '#1a56db',
  Open: '#d97706', Closed: '#16a34a', credit: '#1a56db', debit: '#dc2626',
  'Partially Delivered': '#d97706', Delivered: '#16a34a', Cancelled: '#dc2626',
};

const DateRange = ({ value, onChange }) => (
  <input type="text" readOnly value={value} style={{ ...S.input, width:200, cursor:'pointer', background:'#f9fafb' }}/>
);

const PDFIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="2" width="14" height="20" rx="2" fill="#ef4444" opacity=".15"/>
    <rect x="3" y="2" width="14" height="20" rx="2" stroke="#ef4444" strokeWidth="1.5"/>
    <text x="10" y="15" textAnchor="middle" fill="#ef4444" fontSize="6" fontWeight="bold">PDF</text>
  </svg>
);
const XLSIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="2" width="14" height="20" rx="2" fill="#16a34a" opacity=".15"/>
    <rect x="3" y="2" width="14" height="20" rx="2" stroke="#16a34a" strokeWidth="1.5"/>
    <text x="10" y="15" textAnchor="middle" fill="#16a34a" fontSize="6" fontWeight="bold">XLS</text>
  </svg>
);

const ActionIcons = () => (
  <div style={{ display:'flex', gap:6, alignItems:'center' }}>
    <button style={{ border:'none', background:'none', cursor:'pointer', padding:2 }} title="Export XLS"><XLSIcon/></button>
    <button style={{ border:'none', background:'none', cursor:'pointer', padding:2 }} title="Export PDF"><PDFIcon/></button>
  </div>
);

// ── Gauge chart (Vendor Quality Rating) ──────────────────────
function GaugeChart({ value = 4.5, max = 5 }) {
  const pct   = value / max;
  const angle = -180 + pct * 180;
  const r = 70, cx = 90, cy = 90;
  const toRad = deg => (deg * Math.PI) / 180;
  const arcX  = (deg) => cx + r * Math.cos(toRad(deg));
  const arcY  = (deg) => cy + r * Math.sin(toRad(deg));
  const greenEnd = angle;
  const d = `M ${arcX(-180)} ${arcY(-180)} A ${r} ${r} 0 0 1 ${arcX(0)} ${arcY(0)}`;
  const dG = `M ${arcX(-180)} ${arcY(-180)} A ${r} ${r} 0 ${pct > 0.5 ? 1 : 0} 1 ${arcX(greenEnd)} ${arcY(greenEnd)}`;
  return (
    <svg width="180" height="110" viewBox="0 0 180 110">
      <path d={d} fill="none" stroke="#e2e8f0" strokeWidth="18" strokeLinecap="round"/>
      <path d={dG} fill="none" stroke="#16a34a" strokeWidth="18" strokeLinecap="round"/>
      <text x="90" y="85" textAnchor="middle" fontSize="28" fontWeight="800" fill="#1e2a3b">{value}</text>
      <text x="20" y="105" fontSize="10" fill="#94a3b8">0.00</text>
      <text x="148" y="105" fontSize="10" fill="#94a3b8">5.00</text>
    </svg>
  );
}

// ── Pages ─────────────────────────────────────────────────────

function DashboardPage() {
  const [data, setData] = useState(null);
  useEffect(() => {
    supplyAPI.dashboard().then(r => setData(r.data)).catch(() => setData({
      total_invoices:19, total_credit_notes:3, total_debit_notes:3,
      open_firm_schedule:1, total_sale_value:1137815, vendor_quality_rating:4.5,
      monthly_sales:[
        {month:'May 2025',total:113570},{month:'Jun 2025',total:136070},
        {month:'Jul 2025',total:299470},{month:'Aug 2025',total:100},
        {month:'Sep 2025',total:20250},{month:'Oct 2025',total:19500},
        {month:'Nov 2025',total:9600},{month:'Dec 2025',total:160300},
        {month:'Jan 2026',total:378360},{month:'Feb 2026',total:180},
      ],
    }));
  }, []);

  const [tab, setTab] = useState('overview');
  if (!data) return <div style={{padding:40,color:'#94a3b8'}}>Loading…</div>;

  const deliveryData = [
    { name:'On Time', value:5, color:'#16a34a' },
    { name:'Delay',   value:3, color:'#dc2626' },
    { name:'Delivery Pending', value:3, color:'#d97706' },
  ];
  const qualityData = [
    { name:'QC Pending',   value:1050, color:'#3b82f6' },
    { name:'Approved Qty', value:15550,color:'#16a34a' },
    { name:'Rejected Qty', value:1200, color:'#dc2626' },
    { name:'Rework Qty',   value:200,  color:'#f97316' },
  ];
  const rejectData = data.monthly_sales.map(m => ({
    month: m.month.replace(' 20','\'').replace('2025','25').replace('2026','26'),
    invoice: m.total / 50, rejected: m.total / 300,
  }));

  const StatCard = ({ label, value }) => (
    <div style={{ flex:1, background:'#fff', border:'1px solid #e2e8f0', borderRadius:8, padding:'12px 18px', textAlign:'center' }}>
      <div style={{ fontSize:12, color:'#6b7280', marginBottom:6 }}>{label}</div>
      <div style={{ fontSize:22, fontWeight:800, color:'#1e2a3b' }}>{typeof value==='number'&&value>999 ? value.toLocaleString('en-IN') : value}</div>
    </div>
  );

  return (
    <div>
      <div style={{ fontSize:11, color:'#94a3b8', marginBottom:10 }}>*All figures are in INR</div>

      {/* Tab bar */}
      <div style={{ display:'flex', gap:0, marginBottom:18, justifyContent:'center' }}>
        {['overview','purchase_trend','quality_performance'].map(t => (
          <button key={t} onClick={()=>setTab(t)} style={{
            padding:'8px 22px', border:'1px solid #e2e8f0',
            background: tab===t ? '#e2e8f0' : '#fff',
            cursor:'pointer', fontFamily:'DM Sans,sans-serif', fontSize:13,
            fontWeight: tab===t ? 600 : 400, color:'#1e2a3b',
            borderRadius: t==='overview' ? '6px 0 0 6px' : t==='quality_performance' ? '0 6px 6px 0' : '0',
          }}>
            {t==='overview'?'Overview':t==='purchase_trend'?'Purchase Trend':'Quality Performance'}
          </button>
        ))}
        <span style={{ marginLeft:'auto', fontSize:11, color:'#94a3b8', alignSelf:'center' }}>*Reports as on Year-to-Date (YTD)</span>
      </div>

      {/* KPI cards */}
      <div style={{ display:'flex', gap:12, marginBottom:18 }}>
        <StatCard label="Total Invoice"         value={data.total_invoices} />
        <StatCard label="Total Credit Note"     value={data.total_credit_notes} />
        <StatCard label="Total Debit Note"      value={data.total_debit_notes} />
        <StatCard label="Open Firm Schedule"    value={data.open_firm_schedule} />
        <StatCard label="Total Sale Value (INR)" value={data.total_sale_value} />
      </div>

      {/* Charts row 1 */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16, marginBottom:16 }}>
        {/* Delivery Performance */}
        <div style={S.card}>
          <div style={{ fontWeight:600, fontSize:13, marginBottom:10 }}>Delivery Performance</div>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={deliveryData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" label={({name,value,percent})=>`${value} (${(percent*100).toFixed(2)}%)`} labelLine={false} fontSize={10}>
                {deliveryData.map((d,i) => <Cell key={i} fill={d.color}/>)}
              </Pie>
              <Tooltip/>
              <Legend iconType="circle" iconSize={8} wrapperStyle={{fontSize:11}}/>
            </PieChart>
          </ResponsiveContainer>
        </div>
        {/* Quality Performance */}
        <div style={S.card}>
          <div style={{ fontWeight:600, fontSize:13, marginBottom:10 }}>Quality Performance</div>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={qualityData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" label={({value,percent})=>`${fmtK(value)} (${(percent*100).toFixed(2)}%)`} labelLine={false} fontSize={10}>
                {qualityData.map((d,i) => <Cell key={i} fill={d.color}/>)}
              </Pie>
              <Tooltip/>
              <Legend iconType="circle" iconSize={8} wrapperStyle={{fontSize:11}}/>
            </PieChart>
          </ResponsiveContainer>
        </div>
        {/* Vendor Quality Rating */}
        <div style={{ ...S.card, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
          <div style={{ fontWeight:600, fontSize:13, marginBottom:8 }}>Vendor Quality Rating</div>
          <GaugeChart value={data.vendor_quality_rating}/>
        </div>
      </div>

      {/* Charts row 2 */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
        {/* Monthly Sales */}
        <div style={S.card}>
          <div style={{ fontWeight:600, fontSize:13, marginBottom:10 }}>Monthly Sales</div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data.monthly_sales}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
              <XAxis dataKey="month" tick={{fontSize:10}} tickFormatter={v=>v.split(' ')[0]}/>
              <YAxis tick={{fontSize:10}} tickFormatter={fmtK}/>
              <Tooltip formatter={v=>fmtK(v)}/>
              <Line type="monotone" dataKey="total" stroke="#3b82f6" strokeWidth={2} dot={{r:3}} label={{position:'top',fontSize:9,formatter:fmtK}}/>
            </LineChart>
          </ResponsiveContainer>
        </div>
        {/* Monthly Rejection */}
        <div style={S.card}>
          <div style={{ fontWeight:600, fontSize:13, marginBottom:10 }}>Monthly Rejection</div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={rejectData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
              <XAxis dataKey="month" tick={{fontSize:10}}/>
              <YAxis tick={{fontSize:10}} tickFormatter={fmtK}/>
              <Tooltip formatter={v=>fmtK(v)}/>
              <Legend iconType="circle" iconSize={8} wrapperStyle={{fontSize:11}}/>
              <Area type="monotone" dataKey="invoice"  stroke="#3b82f6" fill="#3b82f620" name="Invoice Qty" label={{position:'top',fontSize:9,formatter:fmtK}}/>
              <Area type="monotone" dataKey="rejected" stroke="#f97316" fill="#f9741620" name="Rejected Qty"/>
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function VendorDetailsPage() {
  const [vendor, setVendor] = useState(null);
  useEffect(() => {
    supplyAPI.getVendor(1).then(r => setVendor(r.data)).catch(() => setVendor({
      vendor_code:'S00005', name:'Iworkkz Vendor Pvt Ltd', contact_person:'Amol Mahajan',
      email:'amol@iworkkz.com', mobile:'8793057134',
      billing_address:'54/2 D-II BLOCK, MIDC, CHINCHWAD Pune 411019 Maharashtra India',
      shipping_address:'54/2 D-II BLOCK, MIDC, CHINCHWAD Pune 411019 Maharashtra India',
      gst_no:'27AAHCS6334B1ZI', gst_type:'Regular/TDS/ISD',
      bank_country:'India', bank_name:'HDFC', account_number:'12345678', ifsc_code:'HDFC0005',
    }));
  }, []);
  if (!vendor) return <div style={{padding:40,color:'#94a3b8'}}>Loading…</div>;
  const FieldRow = ({label,value}) => (
    <tr><td style={{padding:'7px 0',color:'#6b7280',fontSize:13,width:140}}>{label}</td><td style={{padding:'7px 0',color:'#1e2a3b',fontSize:13,fontWeight:500}}>{value||'—'}</td></tr>
  );
  return (
    <div>
      <h2 style={{fontSize:16,fontWeight:700,marginBottom:20}}>Vendor Details</h2>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:20}}>
        <div style={{...S.card,boxShadow:'0 2px 8px rgba(0,0,0,.06)'}}>
          <div style={{fontWeight:700,fontSize:14,borderBottom:'1px solid #f1f5f9',paddingBottom:10,marginBottom:12,textAlign:'center'}}>Details</div>
          <table style={{width:'100%'}}><tbody>
            <FieldRow label="Vendor Code"    value={vendor.vendor_code}/>
            <FieldRow label="Vendor Name"    value={vendor.name}/>
            <FieldRow label="Contact Person" value={vendor.contact_person}/>
            <FieldRow label="Email"          value={vendor.email}/>
            <FieldRow label="Mobile"         value={vendor.mobile}/>
          </tbody></table>
        </div>
        <div style={S.card}>
          <div style={{fontWeight:700,fontSize:14,borderBottom:'1px solid #f1f5f9',paddingBottom:10,marginBottom:12,textAlign:'center'}}>Billing / Shipping Details</div>
          <table style={{width:'100%'}}><tbody>
            <FieldRow label="Billing Address"  value={vendor.billing_address}/>
            <FieldRow label="Shipping Address" value={vendor.shipping_address}/>
            <FieldRow label="GST No"           value={vendor.gst_no}/>
            <FieldRow label="GST Type"         value={vendor.gst_type}/>
          </tbody></table>
        </div>
        <div style={S.card}>
          <div style={{fontWeight:700,fontSize:14,borderBottom:'1px solid #f1f5f9',paddingBottom:10,marginBottom:12,textAlign:'center'}}>Bank Details</div>
          <table style={{width:'100%'}}><tbody>
            <FieldRow label="Bank Country"   value={vendor.bank_country}/>
            <FieldRow label="Bank Name"      value={vendor.bank_name}/>
            <FieldRow label="Account Number" value={vendor.account_number}/>
            <FieldRow label="IFSC Code"      value={vendor.ifsc_code}/>
          </tbody></table>
        </div>
      </div>
    </div>
  );
}

function ASNStatusPage() {
  const [rows,   setRows]   = useState([]);
  const [status, setStatus] = useState('All');
  const [loc,    setLoc]    = useState('All');
  useEffect(() => {
    supplyAPI.listInvoices({ status, location:loc }).then(r => setRows(r.data.data)).catch(() => setRows([
      { id:1, invoice_number:'4566788', invoice_date:'2026-02-10', taxable_amount:5900, tax_amount:1062, total_amount:6962, asn_status:'Booked', gate_entry_no:'30', gate_entry_date:'2026-02-10', grn_number:'24250018', grn_date:'2026-02-10' }
    ]));
  }, [status, loc]);
  return (
    <div>
      <h2 style={{fontSize:16,fontWeight:700,marginBottom:16}}>ASN Status</h2>
      <div style={{display:'flex',alignItems:'center',gap:16,marginBottom:16,flexWrap:'wrap'}}>
        <span><span style={S.label}>Status</span>
          <select style={S.select} value={status} onChange={e=>setStatus(e.target.value)}>
            {['All','Booked','Pending','Rejected','Paid'].map(s=><option key={s}>{s}</option>)}
          </select>
        </span>
        <span><span style={S.label}>Location</span>
          <select style={S.select} value={loc} onChange={e=>setLoc(e.target.value)}>
            {['All','Delhi','CHAKAN','Pune'].map(l=><option key={l}>{l}</option>)}
          </select>
        </span>
        <span style={{marginLeft:'auto'}}><DateRange value="January 1, 2026 - March 31, 2026"/></span>
      </div>
      <div style={S.card}>
        <table style={S.tbl}><thead><tr>
          {['Invoice #','Invoice Date','Taxable Amount','Tax Amount','Total Amount','ASN Status','Star Gate Entry #','Gate Entry Date','Star GRN #','Star GRN Date','Action'].map(h=><th key={h} style={S.th}>{h}</th>)}
        </tr></thead><tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={11} style={{...S.td,textAlign:'center',color:'#94a3b8',padding:30}}>No records found</td></tr>
          ) : rows.map(r => (
            <tr key={r.id}>
              <td style={S.td}>{r.invoice_number}</td>
              <td style={S.td}>{r.invoice_date}</td>
              <td style={S.td}>{fmt(r.taxable_amount)}</td>
              <td style={S.td}>{fmt(r.tax_amount)}</td>
              <td style={S.td}>{fmt(r.total_amount)}</td>
              <td style={S.td}><span style={S.badge(STATUS_COLOR[r.asn_status]||'#6b7280')}>{r.asn_status}</span></td>
              <td style={S.td}>{r.gate_entry_no||'—'}</td>
              <td style={S.td}>{r.gate_entry_date||'—'}</td>
              <td style={S.td}>{r.grn_number||'—'}</td>
              <td style={S.td}>{r.grn_date||'—'}</td>
              <td style={S.td}><ActionIcons/></td>
            </tr>
          ))}
        </tbody></table>
      </div>
    </div>
  );
}

function PurchaseOrderPage() {
  const [rows,   setRows]   = useState([]);
  const [poNo,   setPONo]   = useState('');
  const [status, setStatus] = useState('Open');
  const [loc,    setLoc]    = useState('All');
  const load = () => {
    supplyAPI.listPOs({ po_number:poNo, status, location:loc }).then(r => setRows(r.data.data)).catch(() => setRows([
      {id:1,po_number:'24250020',po_date:'2026-02-03',delivery_date:'2026-02-03',po_type:'-',vendor_ref:'',location:'Delhi',taxable_amount:95000,tax_amount:17100,total_amount:112100,status:'Partially Delivered'},
      {id:2,po_number:'24250021',po_date:'2026-02-05',delivery_date:'2026-02-05',po_type:'-',vendor_ref:'',location:'Delhi',taxable_amount:95000,tax_amount:17100,total_amount:112100,status:'Partially Delivered'},
      {id:3,po_number:'24250022',po_date:'2026-02-10',delivery_date:'2026-02-10',po_type:'-',vendor_ref:'',location:'Delhi',taxable_amount:95000,tax_amount:17100,total_amount:112100,status:'Partially Delivered'},
    ]));
  };
  useEffect(load, []);
  return (
    <div>
      <h2 style={{fontSize:16,fontWeight:700,marginBottom:16}}>Purchase Order Details</h2>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16,flexWrap:'wrap'}}>
        <span><span style={S.label}>PO No.</span><input style={S.input} value={poNo} onChange={e=>setPONo(e.target.value)} placeholder="Search PO #"/></span>
        <span><span style={S.label}>Status</span>
          <select style={S.select} value={status} onChange={e=>setStatus(e.target.value)}>
            {['All','Open','Partially Delivered','Delivered','Cancelled'].map(s=><option key={s}>{s}</option>)}
          </select>
        </span>
        <span><span style={S.label}>Location</span>
          <select style={S.select} value={loc} onChange={e=>setLoc(e.target.value)}>
            {['All','Delhi','CHAKAN','Pune'].map(l=><option key={l}>{l}</option>)}
          </select>
        </span>
        <span style={{marginLeft:'auto',display:'flex',gap:8,alignItems:'center'}}>
          <DateRange value="January 1, 2026 - March 31, 2026"/>
          <button style={{...S.btn,background:'#1a56db',color:'#fff'}} onClick={load}>Search</button>
        </span>
      </div>
      <div style={S.card}>
        <table style={S.tbl}><thead><tr>
          {['PO #','PO Date','Del Date','PO Type','Vendor Ref #','Location','Taxable Amount','Tax Amount','Total Amount','Status','Action'].map(h=><th key={h} style={S.th}>{h}</th>)}
        </tr></thead><tbody>
          {rows.map(r => (
            <tr key={r.id}>
              <td style={S.td}>{r.po_number}</td>
              <td style={S.td}>{r.po_date}</td>
              <td style={S.td}>{r.delivery_date}</td>
              <td style={S.td}>{r.po_type||'-'}</td>
              <td style={S.td}>{r.vendor_ref||''}</td>
              <td style={S.td}>{r.location}</td>
              <td style={S.td}>{fmt(r.taxable_amount)}</td>
              <td style={S.td}>{fmt(r.tax_amount)}</td>
              <td style={S.td}>{fmt(r.total_amount)}</td>
              <td style={S.td}><span style={S.badge(STATUS_COLOR[r.status]||'#6b7280')}>{r.status}</span></td>
              <td style={S.td}><ActionIcons/></td>
            </tr>
          ))}
        </tbody></table>
      </div>
    </div>
  );
}

function InvoicesPage() {
  const [tab, setTab] = useState('booked');
  const [rows, setRows] = useState([]);
  const [file, setFile] = useState(null);
  useEffect(() => {
    supplyAPI.listInvoices({}).then(r => setRows(r.data.data)).catch(() => setRows([
      {id:1,invoice_number:'4566788',invoice_date:'2026-02-10',taxable_amount:5900,tax_amount:1062,total_amount:6962,asn_status:'Booked'}
    ]));
  }, []);
  const sampleCols = ['ItemCode','Quantity','Price','SrNoOfPO','PONo','InvoiceNo','InvoiceDate'];
  return (
    <div>
      <h2 style={{fontSize:16,fontWeight:700,marginBottom:16}}>Invoices</h2>
      <div style={{borderBottom:'1px solid #e2e8f0',marginBottom:20,display:'flex',gap:0}}>
        {[['booked','Booked Invoices'],['upload','Upload Invoices']].map(([k,l])=>(
          <button key={k} onClick={()=>setTab(k)} style={{
            padding:'10px 20px',border:'none',background:'none',cursor:'pointer',
            fontFamily:'DM Sans,sans-serif',fontSize:13,
            color: tab===k ? '#1a56db' : '#6b7280',
            borderBottom: tab===k ? '2px solid #1a56db' : '2px solid transparent',
            fontWeight: tab===k ? 600 : 400,
          }}>{l}</button>
        ))}
      </div>
      {tab === 'booked' ? (
        <div style={S.card}>
          <table style={S.tbl}><thead><tr>
            {['Invoice #','Invoice Date','Taxable Amount','Tax Amount','Total Amount','Status','Action'].map(h=><th key={h} style={S.th}>{h}</th>)}
          </tr></thead><tbody>
            {rows.map(r=>(
              <tr key={r.id}>
                <td style={S.td}>{r.invoice_number}</td>
                <td style={S.td}>{r.invoice_date}</td>
                <td style={S.td}>{fmt(r.taxable_amount)}</td>
                <td style={S.td}>{fmt(r.tax_amount)}</td>
                <td style={S.td}>{fmt(r.total_amount)}</td>
                <td style={S.td}><span style={S.badge(STATUS_COLOR[r.asn_status]||'#6b7280')}>{r.asn_status}</span></td>
                <td style={S.td}><ActionIcons/></td>
              </tr>
            ))}
          </tbody></table>
        </div>
      ) : (
        <div>
          <div style={{background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:8,padding:'12px 18px',marginBottom:20,fontSize:13,color:'#1e40af'}}>
            <div>1. First row must contain column headers exactly as shown.</div>
            <div>2. For date fields use format <strong>YYYY-MM-DD</strong>.</div>
          </div>
          <div style={{display:'flex',justifyContent:'flex-end',marginBottom:12}}>
            <button style={{...S.btn,background:'#16a34a',color:'#fff'}}>⬇ Download Sample (Export)</button>
          </div>
          <div style={S.card}>
            <table style={S.tbl}><thead><tr>
              {sampleCols.map(c=><th key={c} style={S.th}>{c}</th>)}
            </tr></thead><tbody>
              <tr>{sampleCols.map(c=><td key={c} style={{...S.td,height:36}}></td>)}</tr>
            </tbody></table>
            <div style={{display:'flex',alignItems:'center',gap:12,marginTop:16,justifyContent:'flex-end'}}>
              <input type="file" accept=".xlsx,.csv" onChange={e=>setFile(e.target.files[0])} style={{fontSize:13}}/>
              <button style={{...S.btn,background:file?'#1a56db':'#94a3b8',color:'#fff'}} disabled={!file}>Simulate Import</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CreditDebitPage() {
  const [tab,    setTab]    = useState('credit');
  const [rows,   setRows]   = useState([]);
  const [status, setStatus] = useState('Closed');
  const [loc,    setLoc]    = useState('All');
  const load = useCallback(() => {
    supplyAPI.listNotes({ note_type:tab, status, location:loc }).then(r => setRows(r.data.data)).catch(() => setRows([
      {id:1,booking_number:'3',note_date:'2026-01-05',ref_number:'202526097',ref_date:'2025-12-15',taxable_amount:2200,tax_amount:396,total_amount:2596,location:'CHAKAN',status:'Closed',note_type:'credit'},
      {id:2,booking_number:'4',note_date:'2026-01-05',ref_number:'202526099',ref_date:'2025-12-15',taxable_amount:2200,tax_amount:396,total_amount:2596,location:'CHAKAN',status:'Closed',note_type:'debit'},
    ].filter(r=>r.note_type===tab)));
  }, [tab, status, loc]);
  useEffect(load, [load]);
  return (
    <div>
      <h2 style={{fontSize:16,fontWeight:700,marginBottom:16}}>Credit/Debit Notes</h2>
      <div style={{borderBottom:'1px solid #e2e8f0',marginBottom:20,display:'flex',gap:0}}>
        {[['credit','Credit Notes'],['debit','Debit Notes']].map(([k,l])=>(
          <button key={k} onClick={()=>setTab(k)} style={{
            padding:'10px 20px',border:'none',background:'none',cursor:'pointer',
            fontFamily:'DM Sans,sans-serif',fontSize:13,
            color: tab===k ? '#1a56db' : '#6b7280',
            borderBottom: tab===k ? '2px solid #1a56db' : '2px solid transparent',
            fontWeight: tab===k ? 600 : 400,
          }}>{l}</button>
        ))}
      </div>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16,flexWrap:'wrap'}}>
        <span><span style={S.label}>Status</span>
          <select style={S.select} value={status} onChange={e=>setStatus(e.target.value)}>
            {['All','Open','Closed'].map(s=><option key={s}>{s}</option>)}
          </select>
        </span>
        <span><span style={S.label}>Location</span>
          <select style={S.select} value={loc} onChange={e=>setLoc(e.target.value)}>
            {['All','Delhi','CHAKAN','Pune'].map(l=><option key={l}>{l}</option>)}
          </select>
        </span>
        <span style={{marginLeft:'auto',display:'flex',gap:8,alignItems:'center'}}>
          <DateRange value="January 1, 2026 - March 31, 2026"/>
          <button style={{...S.btn,background:'#1a56db',color:'#fff'}} onClick={load}>Search</button>
        </span>
      </div>
      <div style={S.card}>
        <table style={S.tbl}><thead><tr>
          {['Customer Booking #','Note Date','Ref #','Ref Date','Taxable Amount','Tax Amount','Total Amount','Location','Action'].map(h=><th key={h} style={S.th}>{h}</th>)}
        </tr></thead><tbody>
          {rows.map(r=>(
            <tr key={r.id}>
              <td style={S.td}>{r.booking_number}</td>
              <td style={S.td}>{r.note_date}</td>
              <td style={S.td}>{r.ref_number}</td>
              <td style={S.td}>{r.ref_date}</td>
              <td style={S.td}>{fmt(r.taxable_amount)}</td>
              <td style={S.td}>{fmt(r.tax_amount)}</td>
              <td style={S.td}>{fmt(r.total_amount)}</td>
              <td style={S.td}>{r.location}</td>
              <td style={S.td}><ActionIcons/></td>
            </tr>
          ))}
        </tbody></table>
      </div>
    </div>
  );
}

function PaymentPage() {
  const [rows, setRows] = useState([]);
  const load = () => {
    supplyAPI.listPayments({}).then(r => setRows(r.data.data)).catch(() => setRows([
      {id:1,booking_number:'5',payment_date:'2026-01-05',neft_ref:'NE56767',payment_mode:'Bank Transfer',transfer_amount:321668},
      {id:2,booking_number:'6',payment_date:'2026-01-05',neft_ref:'H645677',payment_mode:'Bank Transfer',transfer_amount:2714},
    ]));
  };
  useEffect(load, []);
  return (
    <div>
      <h2 style={{fontSize:16,fontWeight:700,marginBottom:16}}>Payment Received</h2>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16}}>
        <DateRange value="January 1, 2026 - March 31, 2026"/>
        <button style={{...S.btn,background:'#1a56db',color:'#fff'}} onClick={load}>Search</button>
      </div>
      <div style={S.card}>
        <table style={S.tbl}><thead><tr>
          {['Star Booking #','Payment Date','NEFT Ref #','Payment Mode','Transfer Amount','Action'].map(h=><th key={h} style={S.th}>{h}</th>)}
        </tr></thead><tbody>
          {rows.map(r=>(
            <tr key={r.id}>
              <td style={S.td}>{r.booking_number}</td>
              <td style={S.td}>{r.payment_date}</td>
              <td style={S.td}>{r.neft_ref}</td>
              <td style={S.td}>{r.payment_mode}</td>
              <td style={S.td}>{fmt(r.transfer_amount)}</td>
              <td style={S.td}><ActionIcons/></td>
            </tr>
          ))}
        </tbody></table>
      </div>
    </div>
  );
}

function ReportsPage() {
  const reportTypes = [
    { id:'soa',     label:'SOA (PDF)',          icon:'📄', color:'#ef4444' },
    { id:'purchase',label:'Purchase (CSV)',      icon:'📊', color:'#16a34a' },
    { id:'grn',     label:'GRN vs Rejeced (CSV)',icon:'✅', color:'#d97706' },
    { id:'summary', label:'Summary (CSV)',        icon:'📈', color:'#1a56db' },
  ];
  const [selected, setSelected] = useState(null);
  const download = () => {
    if (!selected) { toast.error('Select a report type'); return; }
    toast.success(`Downloading ${reportTypes.find(r=>r.id===selected)?.label}…`);
  };
  return (
    <div>
      <h2 style={{fontSize:16,fontWeight:700,marginBottom:16}}>Reports</h2>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:24}}>
        <div style={{flex:1}}/>
        <DateRange value="January 1, 2026 - March 31, 2026"/>
        <button style={{...S.btn,background:'#1a56db',color:'#fff'}} onClick={download}>Download</button>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:16}}>
        {reportTypes.map(r=>(
          <div key={r.id} onClick={()=>setSelected(r.id)} style={{
            ...S.card, cursor:'pointer', textAlign:'center',
            border: selected===r.id ? `2px solid ${r.color}` : '1px solid #e2e8f0',
            transition:'all .15s',
          }}>
            <div style={{fontSize:40,marginBottom:10}}>{r.icon}</div>
            <div style={{fontSize:13,color:'#374151',fontWeight:500}}>{r.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main iSupplyLink shell ────────────────────────────────────
const NAV = [
  { key:'dashboard', label:'Dashboard' },
  { key:'vendor',    label:'Vendor Details' },
  { key:'asn',       label:'ASN Status' },
  { key:'po',        label:'Open Purchase Order' },
  { key:'invoices',  label:'Invoices' },
  { key:'notes',     label:'Credit/Debit Notes' },
  { key:'payment',   label:'Payment Advice' },
  { key:'reports',   label:'Reports' },
];

const PAGE_MAP = {
  dashboard: DashboardPage,
  vendor:    VendorDetailsPage,
  asn:       ASNStatusPage,
  po:        PurchaseOrderPage,
  invoices:  InvoicesPage,
  notes:     CreditDebitPage,
  payment:   PaymentPage,
  reports:   ReportsPage,
};

export default function ISupplyLinkPage() {
  const [active, setActive] = useState('dashboard');
  const PageComp = PAGE_MAP[active] || DashboardPage;
  return (
    <div style={S.shell}>
      {/* Sidebar */}
      <aside style={S.sidebar}>
        <div style={S.sideHead}>iSupplyLink</div>
        <nav style={{flex:1,overflowY:'auto',paddingTop:8}}>
          {NAV.map(n => (
            <button key={n.key} style={S.navBtn(active===n.key)} onClick={()=>setActive(n.key)}>
              {n.label}
            </button>
          ))}
        </nav>
      </aside>
      {/* Main area */}
      <div style={S.main}>
        <div style={S.topbar}>
          <span style={S.topTitle}>iSupplyLink — Vendor Portal</span>
        </div>
        <div style={S.content}>
          <PageComp/>
        </div>
      </div>
    </div>
  );
}
